import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, ChevronDown } from "lucide-react";
import { adminServices } from "../services/AdminServices";

// Set app element for react-modal
Modal.setAppElement('#root');

const SupplierEditModal = ({ supplier, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    supplierName: "",
    contactName: "",
    email: "",
    contactNo: "",
    faxNo: "",
    paymentMethod: "",
    shipmentPeriod: "",
    address: "",
    city: "",
    region: "",
    status: "Active"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (supplier && supplier.originalData) {
      // Use originalData if available (from API response)
      const original = supplier.originalData;
      setFormData({
        supplierName: original.supplierName || supplier.supplierName || "",
        contactName: original.contactName || supplier.contactPerson || "",
        email: original.email || supplier.email || "",
        contactNo: original.contactNo || supplier.phone || "",
        faxNo: original.faxNo || "",
        paymentMethod: original.paymentMethod || supplier.paymentMethod || "",
        shipmentPeriod: original.shipmentPeriod || supplier.shipmentPeriod || "",
        address: original.address || supplier.address || "",
        city: original.city || "",
        region: original.region || "",
        status: original.status || supplier.status || "Active"
      });
    } else if (supplier) {
      // Fallback to supplier prop if originalData not available
      setFormData({
        supplierName: supplier.supplierName || supplier.name || "",
        contactName: supplier.contactPerson || "",
        email: supplier.email || "",
        contactNo: supplier.phone || "",
        faxNo: "",
        paymentMethod: supplier.paymentMethod || "",
        shipmentPeriod: supplier.shipmentPeriod || "",
        address: supplier.address || "",
        city: "",
        region: "",
        status: supplier.status || "Active"
      });
    }
    setError("");
  }, [supplier]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!supplier) return;

    // Validation
    if (!formData.supplierName || !formData.contactName || !formData.email) {
      setError("Please fill in all required fields (Supplier Name, Contact Name, Email)");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Get supplier ID from originalData or supplier prop
      const supplierId = supplier.originalData?.id || supplier.id || supplier.supplierId;
      
      if (!supplierId) {
        throw new Error("Supplier ID not found");
      }

      // Prepare payload according to API structure
      // Get type information from original data
      const originalType = supplier.originalData?.type;
      const typeId = supplier.originalData?.typeId || originalType?.id || 1;
      
      // Build type object with only required fields (no navigation properties)
      const typeObject = originalType ? {
        id: originalType.id || typeId,
        name: originalType.name || "string",
        regDate: originalType.regDate || new Date().toISOString(),
        status: originalType.status || "string"
        // Note: Do not include 'suppliers' array - it's a navigation property
      } : {
        id: typeId,
        name: "string",
        regDate: new Date().toISOString(),
        status: "string"
      };
      
      const payload = {
        id: supplierId,
        typeId: typeId,
        supplierName: formData.supplierName,
        contactName: formData.contactName,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        region: formData.region,
        paymentMethod: formData.paymentMethod,
        contactNo: formData.contactNo,
        faxNo: formData.faxNo,
        shipmentPeriod: formData.shipmentPeriod,
        regDate: supplier.originalData?.regDate || new Date().toISOString(),
        status: formData.status,
        // Include type object (required by API) - only include required fields
        type: typeObject
        // Note: Do not include 'ipxOrderItems' or 'supplierInventories' - they're navigation properties
      };

      // Call PUT /api/Suppliers/{id}
      const response = await adminServices.updateData(`Suppliers/${supplierId}`, payload);

      if (response.status === 200 || response.status === 204) {
        // Call onSave callback to update parent component
        if (onSave) {
          onSave({
            ...formData,
            supplierName: formData.supplierName,
            contactPerson: formData.contactName,
            phone: formData.contactNo,
            email: formData.email,
            paymentMethod: formData.paymentMethod,
            shipmentPeriod: formData.shipmentPeriod,
            address: formData.address,
            status: formData.status
          });
        }
        onClose();
      } else {
        throw new Error("Failed to update supplier");
      }
    } catch (err) {
      console.error("Error updating supplier:", err);
      setError(err.response?.data?.message || err.message || "Failed to update supplier. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    if (supplier && supplier.originalData) {
      const original = supplier.originalData;
      setFormData({
        supplierName: original.supplierName || supplier.supplierName || "",
        contactName: original.contactName || supplier.contactPerson || "",
        email: original.email || supplier.email || "",
        contactNo: original.contactNo || supplier.phone || "",
        faxNo: original.faxNo || "",
        paymentMethod: original.paymentMethod || supplier.paymentMethod || "",
        shipmentPeriod: original.shipmentPeriod || supplier.shipmentPeriod || "",
        address: original.address || supplier.address || "",
        city: original.city || "",
        region: original.region || "",
        status: original.status || supplier.status || "Active"
      });
    } else if (supplier) {
      setFormData({
        supplierName: supplier.supplierName || supplier.name || "",
        contactName: supplier.contactPerson || "",
        email: supplier.email || "",
        contactNo: supplier.phone || "",
        faxNo: "",
        paymentMethod: supplier.paymentMethod || "",
        shipmentPeriod: supplier.shipmentPeriod || "",
        address: supplier.address || "",
        city: "",
        region: "",
        status: supplier.status || "Active"
      });
    }
    setError("");
    onClose();
  };

  if (!supplier) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          margin: 'auto',
          padding: '0',
          outline: 'none',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      contentLabel="Edit Supplier"
    >
      <div className="supplier-edit-modal-content">
        <button 
          className="supplier-edit-modal-close"
          onClick={handleClose}
        >
          <X size={24} />
        </button>

        <h2 className="supplier-edit-modal-title">
          {supplier.supplierName || supplier.name}
        </h2>

        <div className="supplier-edit-modal-form">
          {error && (
            <div style={{ 
              padding: '12px', 
              marginBottom: '16px', 
              backgroundColor: '#fee', 
              color: '#c33', 
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <div className="supplier-edit-modal-field">
            <label className="supplier-edit-modal-label">Supplier Name *</label>
            <input
              type="text"
              className="supplier-edit-modal-input"
              value={formData.supplierName}
              onChange={(e) => handleChange('supplierName', e.target.value)}
              required
            />
          </div>

          <div className="supplier-edit-modal-field">
            <label className="supplier-edit-modal-label">Contact Name *</label>
            <input
              type="text"
              className="supplier-edit-modal-input"
              value={formData.contactName}
              onChange={(e) => handleChange('contactName', e.target.value)}
              required
            />
          </div>

          <div className="supplier-edit-modal-field">
            <label className="supplier-edit-modal-label">Email *</label>
            <input
              type="email"
              className="supplier-edit-modal-input"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          </div>

          <div className="supplier-edit-modal-row">
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">Contact No</label>
              <input
                type="tel"
                className="supplier-edit-modal-input"
                value={formData.contactNo}
                onChange={(e) => handleChange('contactNo', e.target.value)}
              />
            </div>
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">Fax No</label>
              <input
                type="tel"
                className="supplier-edit-modal-input"
                value={formData.faxNo}
                onChange={(e) => handleChange('faxNo', e.target.value)}
              />
            </div>
          </div>

          <div className="supplier-edit-modal-row">
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">Payment Method</label>
              <input
                type="text"
                className="supplier-edit-modal-input"
                value={formData.paymentMethod}
                onChange={(e) => handleChange('paymentMethod', e.target.value)}
              />
            </div>
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">Shipment Period</label>
              <input
                type="text"
                className="supplier-edit-modal-input"
                value={formData.shipmentPeriod}
                onChange={(e) => handleChange('shipmentPeriod', e.target.value)}
              />
            </div>
          </div>

          <div className="supplier-edit-modal-field">
            <label className="supplier-edit-modal-label">Address</label>
            <input
              type="text"
              className="supplier-edit-modal-input"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </div>

          <div className="supplier-edit-modal-row">
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">City</label>
              <input
                type="text"
                className="supplier-edit-modal-input"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </div>
            <div className="supplier-edit-modal-field supplier-edit-modal-field-half">
              <label className="supplier-edit-modal-label">Region</label>
              <input
                type="text"
                className="supplier-edit-modal-input"
                value={formData.region}
                onChange={(e) => handleChange('region', e.target.value)}
              />
            </div>
          </div>

          <div className="supplier-edit-modal-field">
            <label className="supplier-edit-modal-label">Status</label>
            <div className="supplier-edit-modal-select-wrapper">
              <select
                className="supplier-edit-modal-select"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Inactive">Inactive</option>
              </select>
              <ChevronDown className="supplier-edit-modal-select-icon" size={20} />
            </div>
          </div>
        </div>

        <div className="supplier-edit-modal-actions">
          <button 
            className="supplier-edit-modal-cancel-btn"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="supplier-edit-modal-save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

    </Modal>
  );
};

export default SupplierEditModal;

